import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { TextField } from '../../../components/TextField';
import { Button } from '../../../components/Button/Button';
import { FocusPageLayout } from '../../../components/FocusPageLayout';
import { Paragraph } from '../../../components/Paragraph/Paragraph';
import { Routes } from '../../../enums/Routes';
import { useCreatePartnerMutation } from '../../../hooks/mutations/useCreatePartnerMutation';
import classes from './CreatePartnerPage.module.css';

const PAGE_TITLE = 'Legg til ny partner';
const BACK_LINK_LABEL = 'Tilbake til partneroversikt';

const PARTNER_NAME_LABEL = 'Partnernavn';
const PARTNER_NAME_PLACEHOLDER = 'F.eks. Proactima AS';
const CONTACT_EMAIL_LABEL = 'Kontakt-epost';
const CONTACT_EMAIL_PLACEHOLDER = 'kontakt@partner.no';
const CONTACT_PHONE_LABEL = 'Kontakttelefon (valgfritt)';
const CONTACT_PHONE_PLACEHOLDER = '+47 12345678';
const CASHBACK_PERCENTAGE_LABEL = 'Cashback-prosent (valgfritt)';
const CASHBACK_PERCENTAGE_PLACEHOLDER = 'F.eks. 20 for 20%';
const ADMIN_EMAIL_LABEL = 'E-post til partner-admin';
const ADMIN_EMAIL_PLACEHOLDER = 'admin@partner.no';
const ADMIN_FIRST_NAME_LABEL = 'Fornavn';
const ADMIN_FIRST_NAME_PLACEHOLDER = 'Ola';
const ADMIN_LAST_NAME_LABEL = 'Etternavn';
const ADMIN_LAST_NAME_PLACEHOLDER = 'Nordmann';

const CREATE_BUTTON_TEXT = 'Opprett partner';
const CANCEL_BUTTON_TEXT = 'Avbryt';

const SUCCESS_MESSAGE = 'Partner opprettet! Velkomst-e-post sendt til partner-admin.';
const ERROR_MESSAGE = 'Kunne ikke opprette partner. Prøv igjen senere.';

const createPartnerSchema = z.object({
  name: z.string().min(1, 'Partnernavn er påkrevd'),
  contactEmail: z
    .string()
    .min(1, 'Kontakt-epost er påkrevd')
    .email('Ugyldig e-postadresse'),
  contactPhone: z.string().optional(),
  cashbackPercentage: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        const num = Number(val);
        return !isNaN(num) && num >= 0 && num <= 100;
      },
      { message: 'Cashback-prosent må være mellom 0 og 100' }
    ),
  adminEmail: z
    .string()
    .min(1, 'Admin e-post er påkrevd')
    .email('Ugyldig e-postadresse'),
  adminFirstName: z.string().min(1, 'Fornavn er påkrevd'),
  adminLastName: z.string().min(1, 'Etternavn er påkrevd'),
});

type FormData = z.infer<typeof createPartnerSchema>;

const createEmptyFormData = (): FormData => ({
  name: '',
  contactEmail: '',
  contactPhone: '',
  cashbackPercentage: '',
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
});

type FormErrors = Partial<Record<keyof FormData, string>>;

export const CreatePartnerPage = (): ReactElement => {
  const navigate = useNavigate();
  const createPartnerMutation = useCreatePartnerMutation();

  const [formData, setFormData] = useState<FormData>(createEmptyFormData());
  const [errors, setErrors] = useState<FormErrors>({});

  const updateFormField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCancel = () => {
    navigate(Routes.SystemAdminPartners);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (createPartnerMutation.isPending) {
      return;
    }

    const result = createPartnerSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof FormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const cashbackPercentage = formData.cashbackPercentage?.trim();
    const cashbackBasisPoints = cashbackPercentage
      ? Math.round(Number(cashbackPercentage) * 100)
      : undefined;

    createPartnerMutation.mutate(
      {
        name: formData.name.trim(),
        contactEmail: formData.contactEmail.trim(),
        contactPhone: formData.contactPhone?.trim() || undefined,
        cashbackPercentage: cashbackBasisPoints,
        adminEmail: formData.adminEmail.trim(),
        adminFirstName: formData.adminFirstName.trim(),
        adminLastName: formData.adminLastName.trim(),
      },
      {
        onSuccess: () => {
          toast.success(SUCCESS_MESSAGE);
          navigate(Routes.SystemAdminPartners);
        },
        onError: () => {
          toast.error(ERROR_MESSAGE);
        },
      }
    );
  };

  return (
    <FocusPageLayout
      title={PAGE_TITLE}
      backLink={Routes.SystemAdminPartners}
      backLinkLabel={BACK_LINK_LABEL}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Partnerinformasjon
          </Paragraph>

          <TextField
            name="name"
            label={PARTNER_NAME_LABEL}
            value={formData.name}
            onChange={(e) => updateFormField('name', e.target.value)}
            placeholder={PARTNER_NAME_PLACEHOLDER}
            type="text"
            required
            errorMessage={errors.name}
          />

          <TextField
            name="contactEmail"
            label={CONTACT_EMAIL_LABEL}
            value={formData.contactEmail}
            onChange={(e) => updateFormField('contactEmail', e.target.value)}
            placeholder={CONTACT_EMAIL_PLACEHOLDER}
            type="email"
            required
            errorMessage={errors.contactEmail}
          />

          <TextField
            name="contactPhone"
            label={CONTACT_PHONE_LABEL}
            value={formData.contactPhone}
            onChange={(e) => updateFormField('contactPhone', e.target.value)}
            placeholder={CONTACT_PHONE_PLACEHOLDER}
            type="text"
            errorMessage={errors.contactPhone}
          />

          <TextField
            name="cashbackPercentage"
            label={CASHBACK_PERCENTAGE_LABEL}
            value={formData.cashbackPercentage}
            onChange={(e) => updateFormField('cashbackPercentage', e.target.value)}
            placeholder={CASHBACK_PERCENTAGE_PLACEHOLDER}
            type="number"
            errorMessage={errors.cashbackPercentage}
          />
        </div>

        <div className={classes.section}>
          <Paragraph size="md" className={classes.sectionTitle}>
            Partner-admin bruker
          </Paragraph>
          <Paragraph size="sm" className={classes.sectionDescription}>
            Denne brukeren vil motta en velkomst-e-post med instruksjoner for å sette passord.
            Partner-admin får tilgang til både Trustpulse og partner-panelet.
          </Paragraph>

          <TextField
            name="adminEmail"
            label={ADMIN_EMAIL_LABEL}
            value={formData.adminEmail}
            onChange={(e) => updateFormField('adminEmail', e.target.value)}
            placeholder={ADMIN_EMAIL_PLACEHOLDER}
            type="email"
            required
            errorMessage={errors.adminEmail}
          />

          <div className={classes.nameFields}>
            <TextField
              name="adminFirstName"
              label={ADMIN_FIRST_NAME_LABEL}
              value={formData.adminFirstName}
              onChange={(e) => updateFormField('adminFirstName', e.target.value)}
              placeholder={ADMIN_FIRST_NAME_PLACEHOLDER}
              type="text"
              required
              errorMessage={errors.adminFirstName}
            />

            <TextField
              name="adminLastName"
              label={ADMIN_LAST_NAME_LABEL}
              value={formData.adminLastName}
              onChange={(e) => updateFormField('adminLastName', e.target.value)}
              placeholder={ADMIN_LAST_NAME_PLACEHOLDER}
              type="text"
              required
              errorMessage={errors.adminLastName}
            />
          </div>
        </div>

        <div className={classes.actions}>
          <Button
            variant="secondary"
            size="medium"
            onClick={handleCancel}
            disabled={createPartnerMutation.isPending}
            type="button"
          >
            {CANCEL_BUTTON_TEXT}
          </Button>
          <Button
            variant="primary"
            size="medium"
            type="submit"
            disabled={createPartnerMutation.isPending}
          >
            {CREATE_BUTTON_TEXT}
          </Button>
        </div>
      </form>
    </FocusPageLayout>
  );
};
